import React, { useState } from "react";
import { Badge, Button, Modal } from "react-bootstrap";
import useProfileStore from "../../store/patient/profileStore";
import { useNavigate } from "react-router-dom";
import { parseAvatar } from "../../utils/chat/utils";

const tabs = [
  { id: "personal", label: "Personal", icon: "ri-user-3-line" },
  { id: "insurance", label: "Insurance", icon: "ri-shield-check-line" },
  { id: "kin", label: "Next of Kin", icon: "ri-team-line" },
  { id: "health", label: "Health", icon: "ri-heart-pulse-line" },
];

const displayValue = (value, fallback = "Not provided") =>
  value === undefined || value === null || value === "" ? fallback : value;

const ProfileModal = ({ avatar, show, setShow }) => {
  const navigate = useNavigate();
  const handleClose = () => setShow(false);
  const { profile: data } = useProfileStore();
  const [step, setStep] = useState("personal");

  const heroImage = data?.profile_pic ? parseAvatar(data.profile_pic) : avatar;
  const fullName =
    `${data?.title ? `${data.title} ` : ""}${data?.other_names || ""} ${data?.surname || ""}`.trim() ||
    "Patient Profile";

  const personalDetails = [
    { label: "Full Name", value: fullName },
    { label: "Date of Birth", value: displayValue(data?.date_of_birth) },
    { label: "Gender", value: displayValue(data?.gender) },
    { label: "Phone", value: displayValue(data?.phone_number) },
    { label: "Email", value: displayValue(data?.email) },
    { label: "Address", value: displayValue(data?.address) },
    { label: "Ethnicity", value: displayValue(data?.ethnicity) },
    { label: "Occupation", value: displayValue(data?.occupation) },
  ];

  const insuranceDetails = [
    { label: "Provider", value: displayValue(data?.hmo?.name) },
    { label: "Plan Type", value: displayValue(data?.hmo?.hmoType?.name) },
    { label: "Phone Number", value: displayValue(data?.hmo?.phoneNumber) },
    { label: "Member ID", value: displayValue(data?.enrollee_id || data?.hmo?.id) },
    { label: "Status", value: "Active" },
  ];

  const kinDetails = [
    {
      label: "Full Name",
      value: `${data?.nextOfKin?.other_names || ""} ${data?.nextOfKin?.surname || ""}`.trim(),
    },
    { label: "Relationship", value: displayValue(data?.nextOfKin?.relationship) },
    { label: "Gender", value: displayValue(data?.nextOfKin?.gender) },
    { label: "Phone", value: displayValue(data?.nextOfKin?.phoneNumber) },
    { label: "Occupation", value: displayValue(data?.nextOfKin?.occupation) },
    { label: "Address", value: displayValue(data?.nextOfKin?.address) },
  ];

  const healthDetails = [
    { label: "Blood Group", value: displayValue(data?.blood_group, "--") },
    { label: "Genotype", value: displayValue(data?.genotype, "--") },
    { label: "Hospital ID", value: displayValue(data?.legacy_patient_id || data?.id) },
    { label: "Marital Status", value: displayValue(data?.maritalStatus) },
  ];

  const renderDetails = (items, accent = false) => (
    <div className="profile-modal-grid">
      {items.map((item) => (
        <div className={`profile-modal-card ${accent ? "profile-modal-card--accent" : ""}`} key={item.label}>
          <small>{item.label}</small>
          <strong>{displayValue(item.value)}</strong>
        </div>
      ))}
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      dialogClassName="profile-modal-shell"
    >
      <Modal.Header closeButton className="profile-modal-header">
        <Modal.Title>Patient Profile</Modal.Title>
      </Modal.Header>

      <Modal.Body className="profile-modal-body">
        {data ? (
          <>
            <section className="profile-modal-hero">
              <div className="profile-modal-avatar">
                <img src={heroImage} alt={fullName} />
              </div>

              <div className="profile-modal-copy">
                <span className="profile-modal-kicker">Profile Overview</span>
                <h3>{fullName}</h3>
                <p>
                  A polished summary of your personal record, insurance, emergency
                  contact, and core health identifiers.
                </p>

                <div className="profile-modal-tags">
                  <Badge bg="light" text="dark">
                    {displayValue(data?.gender)}
                  </Badge>
                  <Badge bg="light" text="dark">
                    {displayValue(data?.date_of_birth)}
                  </Badge>
                  <Badge bg="light" text="dark">
                    {displayValue(data?.phone_number)}
                  </Badge>
                </div>
              </div>
            </section>

            <div className="profile-modal-tabs nav-line">
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant="link"
                  className={step === tab.id ? "active is-active" : ""}
                  onClick={() => setStep(tab.id)}
                >
                  <i className={tab.icon}></i>
                  <span>{tab.label}</span>
                </Button>
              ))}
            </div>

            <section className="profile-modal-panel">
              {step === "personal" && (
                <>
                  <div className="profile-modal-section-heading">
                    <h6>Personal Details</h6>
                    <p>Core identity and contact information used across your care journey.</p>
                  </div>
                  {renderDetails(personalDetails)}
                </>
              )}

              {step === "insurance" && (
                <>
                  <div className="profile-modal-section-heading">
                    <h6>Insurance & HMO</h6>
                    <p>Your provider, plan information, and active coverage snapshot.</p>
                  </div>

                  {data?.hmo?.logo && (
                    <div className="profile-hmo-logo">
                      <img src={data.hmo.logo} alt={data?.hmo?.name || "HMO logo"} />
                    </div>
                  )}

                  {renderDetails(insuranceDetails, true)}
                </>
              )}

              {step === "kin" && (
                <>
                  <div className="profile-modal-section-heading">
                    <h6>Emergency Contact</h6>
                    <p>The person your care team can reach if urgent support is needed.</p>
                  </div>
                  {renderDetails(kinDetails)}
                </>
              )}

              {step === "health" && (
                <>
                  <div className="profile-modal-section-heading">
                    <h6>Health Identifiers</h6>
                    <p>Important baseline details that support safer clinical decisions.</p>
                  </div>
                  {renderDetails(healthDetails, true)}
                </>
              )}
            </section>
          </>
        ) : (
          <p className="mb-0">No patient data available.</p>
        )}
      </Modal.Body>

      <Modal.Footer className="profile-modal-footer">
        <Button className="btn-deda" onClick={() => navigate("/pages/profile")}>
          Edit Profile
        </Button>
        <Button variant="" className="btn-white" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProfileModal;
